import React, { useState, useEffect } from 'react';
import { Layout } from "./components/dashboard/layout";
import { Box, TextField, Typography, Button } from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const VisitorReports = () => {
  const [visitors, setVisitors] = useState([]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [companyName, setCompanyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchVisitors = async () => {
    setIsLoading(true);
    try {
      const from = formatDate(fromDate);
      const to = formatDate(toDate);
      const company = encodeURIComponent(companyName);

      const url = `https://raptornet.aischennai.org/screening-manager/gate-api/getVisitors.php?from_date=${from}&to_date=${to}&company_name=${company}`;
      const response = await fetch(url);
      const result = await response.json();

      if (result.status === 'success') {
        setVisitors(result.data);
      } else {
        setVisitors([]);
      }
    } catch (error) {
      console.error("Error fetching visitors:", error);
      setVisitors([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitors();
  }, []);

  // ---------------------------
  // CSV Export Handler
  // ---------------------------
  const exportToCSV = () => {
    if (!visitors.length) {
      alert("No data to export!");
      return;
    }

    // Define CSV headers (you can adjust columns as needed)
    const headers = [
      "ID", "Date Created", "Name", "Company", "Visitor Type", "Visiting Host",
      "Host Department", "Purpose", "Status", "Check-In", "Check-Out"
    ];

    // Convert data to CSV rows
    const rows = visitors.map(v => [
      v.ID, v.date_created, v.name, v.company_name, v.visitor_type, v.visiting_host,
      v.host_department, v.purpose_of_visit, v.status, 
      v.check_in_time, v.check_out_time
    ]);

    // Combine headers + rows
    let csvContent = "data:text/csv;charset=utf-8," 
      + [headers, ...rows].map(e => e.map(x => `"${x || ''}"`).join(",")).join("\n");

    // Create and trigger download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Visitor_Report_${formatDate(fromDate)}_to_${formatDate(toDate)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>Visitor Reports</Typography>

        {/* Search Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', marginTop: '50px' }}>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            dateFormat="yyyy-MM-dd"
            customInput={<TextField variant="outlined" label="From Date" />}
          />
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            dateFormat="yyyy-MM-dd"
            customInput={<TextField variant="outlined" label="To Date" />}
          />
          <TextField
            label="Search by Company"
            variant="outlined"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
          <Button variant="contained" onClick={fetchVisitors} disabled={isLoading}>
            {isLoading ? "Loading..." : "Search"}
          </Button>

          {/* Export CSV Button */}
          <Button 
            variant="outlined" 
            color="success" 
            onClick={exportToCSV}
            disabled={!visitors.length}
          >
            Export CSV
          </Button>
        </Box>

        {/* Table */}
        <Box sx={{ mt: 3 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={th}>Date Created</th>
                <th style={th}>Name</th>
                <th style={th}>Company</th>
                <th style={th}>Visitor Type</th>
                <th style={th}>Visiting Host</th>
                <th style={th}>Host Department</th>
                <th style={th}>Purpose</th>
                <th style={th}>Status</th>
                <th style={th}>Check-In</th>
                <th style={th}>Check-Out</th>
              </tr>
            </thead>
            <tbody>
              {visitors.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                    {isLoading ? "Loading..." : "No visitors found."}
                  </td>
                </tr>
              ) : (
                visitors.map((v, index) => (
                  <tr key={v.ID || index}>
                    <td style={td}>{index + 1}</td>
                    <td style={td}>{v.date_created}</td>
                    <td style={td}>{v.name}</td>
                    <td style={td}>{v.company_name}</td>
                    <td style={td}>{v.visitor_type}</td>
                    <td style={td}>{v.visiting_host}</td>
                    <td style={td}>{v.host_department}</td>
                    <td style={td}>{v.purpose_of_visit}</td>
                    <td style={td}>{v.status}</td>
                    <td style={td}>{v.check_in_time}</td>
                    <td style={td}>{v.check_out_time}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      </Box>
    </Layout>
  );
};

// Inline table styling
const th = { borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left', background: '#f9f9f9' };
const td = { borderBottom: '1px solid #ddd', padding: '8px' };

export default VisitorReports;
